import React from 'react';
import { SVGAssets } from '@business/assets/vectors';
import { Button } from '../Button/Button';

export interface ConfirmationBoxProps {
  isOpen: boolean;
  onClose: () => void;
  onCancel: () => void;
  onConfirm?: () => void;
  headerTitle?: string;
  title: string;
  description?: string;
  icon?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  showCloseButton?: boolean;
  className?: string;
  isLoading?: boolean;
  progressText?: string;
  progress?: number;
}

export const ConfirmationBox: React.FC<ConfirmationBoxProps> = ({
  isOpen,
  onClose,
  onCancel,
  onConfirm,
  headerTitle = 'Upload Files',
  title,
  description,
  icon,
  confirmText = 'Allow access',
  cancelText = 'Skip for now',
  showCloseButton = true,
  className = '',
  isLoading = false,
  progressText,
  progress
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div
        className={`relative w-full max-w-md confirmation-box-style bg-white shadow-lg ${className}`}>
        <div className="flex justify-between items-center px-5 py-4">
          <h3 className="text-lg font-medium text-[#505050]">{headerTitle}</h3>
          {showCloseButton && (
            <Button
              btnStyle="link"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              data-testid="close-button">
              <SVGAssets.CloseIconGray className="w-5 h-4 text-gray-400" />
            </Button>
          )}
        </div>
        <hr className="border-t border-gray-200 w-full" />

        <div className="flex flex-col items-center text-center px-5 py-6">
          {icon && <div className="mb-4">{icon}</div>}
          <h2
            className="mb-2 text-xl font-semibold text-gray-800"
            data-testid="confirmation-title">
            {title}
          </h2>

          {description && (
            <p
              className="mb-6 text-sm text-gray-600"
              data-testid="confirmation-description">
              {description}
            </p>
          )}

          {isLoading && progress !== undefined && (
            <div className="mb-4 w-full">
              <div className="h-2 w-full rounded-full bg-gray-200">
                <div
                  className="h-full rounded-full bg-green-600 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                  data-testid="progress-bar"
                />
              </div>
              {progressText && (
                <p
                  className="mt-2 text-sm text-gray-600"
                  data-testid="progress-text">
                  {progressText}
                </p>
              )}
            </div>
          )}

          <div className="flex w-full justify-center items-center gap-3 mt-4">
            {cancelText && (
              <Button
                onClick={onCancel}
                btnStyle="link"
                className="flex-1 px-6 py-2 !bg-[#F9FEE9] text-[var(--na-primary-color)] font-medium rounded-md hover:bg-[#F9FEE9] text-sm"
                data-testid="cancel-button"
                isDisabled={isLoading}>
                {cancelText}
              </Button>
            )}

            {confirmText && (
              <Button
                onClick={onConfirm}
                btnStyle="primary"
                className="flex-1 py-2 rounded-md text-sm"
                data-testid="confirm-button"
                isDisabled={isLoading}>
                {confirmText}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
